import { useCallback, useEffect, useState } from 'react';
import { BellRing, Trash2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import CryptoIcon from './CryptoIcon';
import { priceAlertsAPI } from '../services/api';
import { PRICE_ALERTS_CHANGED } from './PriceAlertButton';

const fmt = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const AlertRow = ({ alert, onDelete, onReactivate }) => {
  const isActive = alert.active;
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-800/50" data-testid={`price-alert-row-${alert.id}`}>
      <CryptoIcon symbol={alert.symbol} size={30} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white">
          {alert.symbol}/USD {alert.condition === 'above' ? '≥' : '≤'} {fmt(alert.target_price)}
        </p>
        <p className={`text-xs ${isActive ? 'text-emerald-400' : 'text-amber-400'}`} data-testid={`price-alert-status-${alert.id}`}>
          {isActive ? 'Watching' : `Triggered at ${fmt(alert.triggered_price)}`}
        </p>
      </div>
      {!isActive && (
        <button type="button" onClick={() => onReactivate(alert.id)} title="Turn back on"
          data-testid={`price-alert-reactivate-${alert.id}`}
          className="p-2 rounded-lg text-purple-300 hover:bg-purple-500/15 transition-colors">
          <RotateCcw className="h-4 w-4" />
        </button>
      )}
      <button type="button" onClick={() => onDelete(alert.id)} title="Delete"
        data-testid={`price-alert-delete-${alert.id}`}
        className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors">
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
};

export const PriceAlertsPanel = () => {
  const [alerts, setAlerts] = useState([]);
  const [maxActive, setMaxActive] = useState(10);

  const load = useCallback(async () => {
    try {
      const data = await priceAlertsAPI.list();
      setAlerts(data.alerts || []);
      setMaxActive(data.max_active || 10);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    window.addEventListener(PRICE_ALERTS_CHANGED, load);
    return () => window.removeEventListener(PRICE_ALERTS_CHANGED, load);
  }, [load]);

  const run = async (fn, okMsg) => {
    try {
      await fn();
      toast.success(okMsg);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const activeCount = alerts.filter((a) => a.active).length;

  return (
    <div className="glow-card rounded-xl p-4 mt-4" data-testid="price-alerts-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="flex items-center gap-2 text-white font-bold">
          <BellRing className="h-4 w-4 text-amber-400" /> Metal Price Alerts
        </h3>
        <span className="text-xs text-gray-400" data-testid="price-alerts-count">{activeCount}/{maxActive} active</span>
      </div>
      {alerts.length === 0 ? (
        <p className="text-sm text-gray-500" data-testid="price-alerts-empty">
          Select Gold, Silver or Platinum and tap &quot;Price Alert&quot; to get notified at your target price.
        </p>
      ) : (
        <div className="space-y-2 max-h-[320px] overflow-y-auto">
          {alerts.map((a) => (
            <AlertRow
              key={a.id}
              alert={a}
              onDelete={(id) => run(() => priceAlertsAPI.remove(id), 'Alert deleted')}
              onReactivate={(id) => run(() => priceAlertsAPI.reactivate(id), 'Alert turned back on')}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PriceAlertsPanel;
